import { ConditionOperatorEnum, type Condition } from '@repo/api-gw-sdk';

import {
  getRelativeTime,
  RelativeTimeOption,
} from '@/data/inventory/relativeTime';
import { createFromCondition, createToCondition } from '@/types/advanceFilter';
import { dayjs, type Dayjs } from '@/utils/dayjs';

import {
  ALL_TIME,
  type DateFilterProperty,
  type TimeFilterValue,
} from './dateFilterTypes';

export const DEFAULT_RELATIVE_TIME = RelativeTimeOption.Last2Weeks;

const KNOWN_RELATIVE_KEYS: ReadonlySet<string> = new Set([
  ...Object.values(RelativeTimeOption),
  ALL_TIME,
]);

/** A stored key can come from a hand-written URL, and an unknown one has no menu entry to select. */
export function toRelativeValue(
  relativeKey: string | undefined
): TimeFilterValue {
  return relativeKey && KNOWN_RELATIVE_KEYS.has(relativeKey)
    ? (relativeKey as TimeFilterValue)
    : ALL_TIME;
}

/** The from/to bounds a set of date conditions applies, for a caller that filters by them. */
export function readDateFilterRange(conditions: Condition[]): {
  from?: Dayjs;
  to?: Dayjs;
} {
  const bound = (operator: ConditionOperatorEnum) => {
    const condition = conditions.find((c) => c.operator === operator);
    return condition ? dayjs.utc(condition.value?.[0]) : undefined;
  };
  return {
    from: bound(ConditionOperatorEnum.From),
    to: bound(ConditionOperatorEnum.To),
  };
}

/**
 * Which property the applied conditions filter on. Falls back for the two cases
 * that carry no answer: nothing applied, and a property the caller no longer
 * offers (a stale link).
 */
export function readSelectedProperty(
  conditions: Condition[],
  properties: DateFilterProperty[],
  fallback: string
): string {
  if (!conditions.length) {
    return fallback;
  }
  const applied = properties.find((p) => p.value === conditions[0].property);
  return applied?.value ?? fallback;
}

/** The span a freshly enabled filter starts with. */
export function defaultPresetConditions(property: string): Condition[] {
  return [
    createFromCondition(
      property,
      getRelativeTime(DEFAULT_RELATIVE_TIME).toISOString()
    ),
  ];
}

/**
 * The conditions a preset applies. "Between" keeps whichever bounds are already
 * entered rather than inventing any, so switching to it before picking dates
 * applies no filter at all — the caller shows the date inputs off its stored key,
 * not off these conditions.
 */
export function presetConditions(
  property: string,
  presetKey: Exclude<TimeFilterValue, typeof ALL_TIME>,
  bounds: { from?: Dayjs; to?: Dayjs }
): Condition[] {
  if (presetKey !== RelativeTimeOption.Between) {
    return [
      createFromCondition(property, getRelativeTime(presetKey).toISOString()),
    ];
  }
  const between: Condition[] = [];
  if (bounds.from) {
    between.push(createFromCondition(property, bounds.from.toISOString()));
  }
  if (bounds.to) {
    between.push(createToCondition(property, bounds.to.toISOString()));
  }
  return between;
}

/** Retargets an applied span at another property, keeping the dates it covers. */
export function retargetConditions(
  conditions: Condition[],
  property: string
): Condition[] {
  return conditions.map((condition) => ({ ...condition, property }));
}

/** Replaces one end of the span, leaving the other end and any other property alone. */
export function replaceBound(
  conditions: Condition[],
  property: string,
  operator: ConditionOperatorEnum,
  value: Dayjs
): Condition[] {
  const replacement =
    operator === ConditionOperatorEnum.From
      ? createFromCondition(property, value.toISOString())
      : createToCondition(property, value.toISOString());
  return [
    ...conditions.filter(
      (c) => !(c.property === property && c.operator === operator)
    ),
    replacement,
  ];
}
